// BotList.tsx - SIMPLE VERSION: No cache, just show stored template names
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { getBotList, updateBotStatus, deleteBot, type Bot as ServiceBot } from '../../services/botService';
import GenericConfirmationModal from '../../components/GenericConfirmationModal';
import Header from '../../components/header';
import { Container } from 'reactstrap';
import styles from './BotList.module.css';
import logger from '../../utils/logger';

// Simple bot interface - no template complexity
interface BotListItem {
  metrics: any;
  _id: string;
  name: string;
  objective?: string | null;
  status: string;
  botType: string | null;
  triggerCondition?: {
    keywords?: string[];
  };
  groups?: Array<{
    steps?: Array<{
      templateId?: string;
      metadata?: {
        templateId?: string;
        templateName?: string;
      };
    }>;
  }>;
  createdAt: string;
  lastTriggerAt?: string;
}

interface BotListProps {
  onCreateNew: () => void;
  onEditBot: (botId: string) => void;
}

// SIMPLE: Just show stored template name, no API calls
const TemplateNameDisplay: React.FC<{ bot: BotListItem }> = ({ bot }) => {
  // Get template name from stored bot data - NO API CALLS
  const templateName = bot.groups?.[0]?.steps?.[0]?.metadata?.templateName;
  
  // Just display what we have stored
  return (
    <div className={styles.templateDisplay}>
      <span className={styles.templateName}>
        {templateName || 'No template'}
      </span>
    </div>
  );
};

const BotList: React.FC<BotListProps> = ({ onCreateNew, onEditBot }) => {
  logger.log('📋 BotList: Component rendered - SIMPLE VERSION');

  const [bots, setBots] = useState<BotListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [deletingBot, setDeletingBot] = useState<string | null>(null);
  
  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [botToDelete, setBotToDelete] = useState<{ id: string; name: string } | null>(null);
  
  const perPage = 10;

  logger.log('📊 BotList: Current state:', { 
    botsCount: bots.length, 
    loading, 
    searchQuery, 
    currentPage,
  });

  // SIMPLE: Just fetch bots, no template pre-fetching
  const fetchBots = async () => {
    logger.log('🔄 BotList: Fetching bots - no template caching...');
    setLoading(true);

    try {
      const response = await getBotList({
        search: searchQuery,
        page: currentPage,
        perPage: perPage,
        orderBy: '_id',
        isAscending: false,
      });

      logger.log('✅ BotList: Bots fetched:', response.data);

      // Transform to UI type - simple mapping
      const keywordBots: BotListItem[] = response.data
        .map((bot: ServiceBot): BotListItem => ({
          _id: bot._id,
          name: bot.name,
          objective: bot.objective ?? null,
          status: bot.status,
          botType: bot.botType ?? null,
          triggerCondition: bot.triggerCondition,
          groups: bot.groups,
          createdAt: bot.createdAt || new Date().toISOString(),
          lastTriggerAt: bot.lastTriggerAt,
          metrics: {
      numberOfRuns: bot.metrics?.numberOfRuns || 0,
      numberOfLeads: bot.metrics?.numberOfLeads || 0
    },
        }));

      setBots(keywordBots);
      setTotalItems(response.total || 0);

      logger.log('✅ BotList: Bots processed, no template operations needed');

    } catch (error) {
      console.error('❌ BotList: Error fetching bots:', error);
      toast.error('Failed to load bots');
    } finally {
      setLoading(false);
    }
  };

  // Load bots on mount and when filters change
  useEffect(() => {
    fetchBots();
  }, [searchQuery, currentPage]);

  // Handle bot status toggle
  const handleToggleStatus = async (botId: string, currentStatus: string) => {
    logger.log('🔄 BotList: Toggling bot status:', { botId, currentStatus });
    
    setUpdatingStatus(botId);

    try {
      const newStatus = currentStatus === 'active' ? 'draft' : 'active';
      
      await updateBotStatus(botId, newStatus);
      logger.log('✅ BotList: Bot status updated successfully');

      // Update local state
      setBots(prevBots => 
        prevBots.map(bot => 
          bot._id === botId ? { ...bot, status: newStatus } : bot
        )
      );

      toast.success(`Bot ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);

    } catch (error) {
      console.error('❌ BotList: Error updating bot status:', error);
      toast.error('Failed to update bot status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Handle bot deletion with confirmation
  const handleDeleteBot = (botId: string, botName: string) => {
    logger.log('🗑️ BotList: Preparing to delete bot:', { botId, botName });
    setBotToDelete({ id: botId, name: botName });
    setShowDeleteModal(true);
  };

  // Confirm bot deletion
  const confirmDeleteBot = async () => {
    if (!botToDelete) return;

    logger.log('🗑️ BotList: Confirming deletion of bot:', botToDelete);
    setDeletingBot(botToDelete.id);

    try {
      await deleteBot(botToDelete.id);
      logger.log('✅ BotList: Bot deleted successfully');

      // Remove from local state
      setBots(prevBots => prevBots.filter(bot => bot._id !== botToDelete.id));
      setTotalItems(prev => prev - 1);

      toast.success('Bot deleted successfully');

    } catch (error) {
      console.error('❌ BotList: Error deleting bot:', error);
      toast.error('Failed to delete bot');
    } finally {
      setDeletingBot(null);
      setBotToDelete(null);
      setShowDeleteModal(false);
    }
  };

  // Cancel bot deletion
  const cancelDeleteBot = () => {
    logger.log('❌ BotList: Bot deletion cancelled by user');
    setBotToDelete(null);
    setShowDeleteModal(false);
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get keywords display
  const getKeywordsDisplay = (bot: BotListItem): string => {
    const keywords = bot.triggerCondition?.keywords || [];
    if (keywords.length === 0) return 'No keywords';
    if (keywords.length <= 3) return keywords.join(', ');
    return `${keywords.slice(0, 3).join(', ')} +${keywords.length - 3} more`;
  };

  if (loading && bots.length === 0) {
    return (
      <div id="main" className="main">
        <Header />
        <Container fluid className="py-4">
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading bots...</p>
          </div>
        </Container>
      </div>
    );
  }

// 🔥 FULL RETURN STATEMENT for BotList component with performance columns
  return (
    <div id="main" className="main">
      <Header />
      <Container fluid className="py-4">
        <div className={styles.botList}>
        
        {/* Header */}
        <div className={styles.listHeader}>
          <div>
            <h1>Keyword Response Bots</h1>
            <p>Set up Whatsapp bots that automatically send WhatsApp template messages when a client messages you with specific keywords

</p>
          </div>
          
          <button 
            className={styles.createButton}
            onClick={onCreateNew}
          >
            + Create New Bot
          </button>
        </div>

        {/* Search and Filters */}
        <div className={styles.filtersContainer}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder="Search bots..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            <i className="fa fa-search"></i>
          </div>
          
          <div className={styles.statsInfo}>
            {totalItems > 0 && (
              <span>{totalItems} bot{totalItems !== 1 ? 's' : ''} found</span>
            )}
          </div>
        </div>

        {/* Bots Table */}
        {bots.length > 0 ? (
          <div className={styles.botTableContainer}>
            {/* Table Header */}
            <div className={styles.tableHeader}>
              <div className={styles.headerCell} style={{ flex: '2' }}>Bot Name</div>
              <div className={styles.headerCell} style={{ flex: '1.5' }}>Keywords</div>
              <div className={styles.headerCell} style={{ flex: '1.5' }}>Template</div>
              <div className={styles.headerCell} style={{ flex: '1' }}>Runs</div>
             
              <div className={styles.headerCell} style={{ flex: '1.2' }}>Last Run</div>
              <div className={styles.headerCell} style={{ flex: '1' }}>Status</div>
              <div className={styles.headerCell} style={{ flex: '0.8' }}>Actions</div>
            </div>

            {/* Bot Rows */}
            <div className={styles.botRows}>
              {bots.map((bot) => (
                <div key={bot._id} className={styles.botRow}>
                  
                  {/* Bot Name and Description */}
                  <div className={styles.botNameCell} style={{ flex: '2' }}>
                    <div className={styles.botName}>{bot.name}</div>
                    {bot.objective && (
                      <div className={styles.botDescription}>{bot.objective}</div>
                    )}
                  </div>

                  {/* Keywords */}
                  <div className={styles.keywordsCell} style={{ flex: '1.5' }}>
                    <span className={styles.keywordsDisplay}>
                      {getKeywordsDisplay(bot)}
                    </span>
                  </div>

                  {/* Template name */}
                  <div className={styles.templateCell} style={{ flex: '1.5' }}>
                    <TemplateNameDisplay bot={bot} />
                  </div>

                  {/* 🔥 NEW: Number of Runs */}
                  <div className={styles.metricsCell} style={{ flex: '1' }}>
                    <div className={styles.metricValue}>
                      {bot.metrics?.numberOfRuns || 0}
                    </div>
                    <div className={styles.metricLabel}>runs</div>
                  </div>

                 

                  {/* 🔥 NEW: Last Triggered */}
                  <div className={styles.lastRunCell} style={{ flex: '1.2' }}>
                    <span className={styles.dateText}>
                      {bot.lastTriggerAt ? formatDate(bot.lastTriggerAt) : 'Never'}
                    </span>
                  </div>

                  {/* Status Toggle */}
                  <div className={styles.statusCell} style={{ flex: '1' }}>
                    <div className={styles.statusContainer}>
                      <label className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          checked={bot.status === 'active'}
                          onChange={() => handleToggleStatus(bot._id, bot.status)}
                          disabled={updatingStatus === bot._id}
                        />
                        <span className={styles.toggleSlider}></span>
                      </label>
                      
                      <span className={styles.statusLabel}>
                        {updatingStatus === bot._id ? 'Updating...' : 
                         bot.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className={styles.actionsCell} style={{ flex: '0.8' }}>
                    <div className={styles.actionButtons}>
                      <button
                        onClick={() => onEditBot(bot._id)}
                        className={styles.editButton}
                        title="Edit Bot"
                      >
                        <i className="fa fa-edit"></i>
                      </button>
                      
                      <button
                        onClick={() => handleDeleteBot(bot._id, bot.name)}
                        className={styles.deleteButton}
                        disabled={deletingBot === bot._id}
                        title="Delete Bot"
                      >
                        {deletingBot === bot._id ? (
                          <i className="fa fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fa fa-trash"></i>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>🤖</div>
            <h3>No keyword bots found</h3>
            <p>
              {searchQuery 
                ? `No bots match "${searchQuery}". Try a different search term.`
                : 'Create your first keyword response bot to get started.'
              }
            </p>
            
            {!searchQuery && (
              <button 
                className={styles.createButton}
                onClick={onCreateNew}
              >
                Create Your First Bot
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalItems > perPage && (
          <div className={styles.pagination}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className={styles.paginationButton}
            >
              Previous
            </button>
            
            <span className={styles.paginationInfo}>
              Page {currentPage} of {Math.ceil(totalItems / perPage)}
            </span>
            
            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={currentPage >= Math.ceil(totalItems / perPage)}
              className={styles.paginationButton}
            >
              Next
            </button>
          </div>
        )}
        </div>

        {/* Delete Confirmation Modal */}
        <GenericConfirmationModal
          showModal={showDeleteModal}
          toggleModal={cancelDeleteBot}
          onConfirmation={confirmDeleteBot}
          title="Delete Bot"
          message={botToDelete ? `Are you sure you want to delete "${botToDelete.name}"?\n\nThis action cannot be undone and will permanently remove the bot and all its configurations.` : ''}
          confirmText="Delete Bot"
          cancelText="Cancel"
          isWarning={true}
        />
      </Container>
    </div>
  );
};

export default BotList;